import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { CreateInvoiceInput } from '@/types/domain/invoice-management';
import { PurchaseOrderRecord } from '@/types/domain/po-management';
import { mockPurchaseOrderService } from '@/services/mock/MockPurchaseOrderService';
import { Receipt, X, RefreshCw, CheckCircle2, AlertTriangle } from 'lucide-react';

interface InvoiceCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (input: CreateInvoiceInput) => Promise<void>;
  initialPoId?: string;
  initialGrnId?: string;
  initialPoNumber?: string;
}

export const InvoiceCreateModal: React.FC<InvoiceCreateModalProps> = ({
  isOpen,
  onClose,
  onCreated,
  initialPoId,
  initialGrnId,
  initialPoNumber,
}) => {
  const [availablePos, setAvailablePos] = useState<PurchaseOrderRecord[]>([]);
  const [isLoadingPos, setIsLoadingPos] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [formData, setFormData] = useState<CreateInvoiceInput>({
    vendorInvoiceNumber: `VND-INV-${Math.floor(10000 + Math.random() * 90000)}`,
    vendorId: '',
    vendorName: '',
    vendorCode: '',
    poId: initialPoId || '',
    poNumber: initialPoNumber || '',
    grnId: initialGrnId,
    currency: 'INR',
    invoiceDate: new Date().toISOString().slice(0, 10),
    receivedDate: new Date().toISOString().slice(0, 10),
    dueDate: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    paymentTerms: 'Net 30 Days',
    shippingAmount: 0,
    otherCharges: 0,
    discountAmount: 0,
    notes: 'Standard inward vendor invoice receipt.',
    items: [],
  });

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const loadPos = async () => {
        try {
          setIsLoadingPos(true);
          const res = await mockPurchaseOrderService.getPurchaseOrders({ page: 1, pageSize: 50, status: 'ALL', sapStatus: 'ALL', plant: 'ALL', buyer: 'ALL' });
          setAvailablePos(res.items);
          if (initialPoId || initialPoNumber) {
            const matched = res.items.find(
              (p) => (initialPoId && p.id === initialPoId) || (initialPoNumber && p.poNumber === initialPoNumber)
            );
            if (matched) {
              handleSelectPo(matched);
            }
          }
        } finally {
          setIsLoadingPos(false);
        }
      };
      loadPos();
    } else {
      document.body.style.overflow = '';
    }
  }, [isOpen, initialPoId, initialPoNumber]);

  if (!isOpen) return null;

  const handleSelectPo = (po: PurchaseOrderRecord) => {
    setFormData((prev) => ({
      ...prev,
      poId: po.id,
      poNumber: po.poNumber,
      vendorId: po.vendorId,
      vendorName: po.vendorName,
      vendorCode: po.vendorCode,
      currency: po.currency,
      plantCode: po.plant,
      grnId: po.grnIds[0],
      paymentTerms: po.paymentTerms,
      items: po.items.map((it) => ({
        poItemId: it.id,
        lineNumber: it.lineNumber,
        materialCode: it.materialCode,
        description: it.description,
        quantity: it.quantityOrdered,
        uom: it.uom,
        unitPrice: it.unitPrice,
        taxRate: it.taxPercent,
        discount: 0,
      })),
    }));
  };

  const handleItemQtyChange = (index: number, val: number) => {
    setFormData((prev) => {
      const updated = [...prev.items];
      updated[index] = { ...updated[index]!, quantity: Math.max(0, val) };
      return { ...prev, items: updated };
    });
  };

  const handleItemPriceChange = (index: number, val: number) => {
    setFormData((prev) => {
      const updated = [...prev.items];
      updated[index] = { ...updated[index]!, unitPrice: Math.max(0, val) };
      return { ...prev, items: updated };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.poNumber) {
      setErrorMessage('Please select a valid source Purchase Order.');
      return;
    }
    if (!formData.vendorInvoiceNumber.trim()) {
      setErrorMessage('Vendor Invoice Number is required.');
      return;
    }
    if (formData.items.length === 0) {
      setErrorMessage('No line items present on this invoice.');
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage(null);
      await onCreated(formData);
      onClose();
    } catch (err: unknown) {
      setErrorMessage((err as Error).message || 'Failed to create invoice.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 font-heading">
      <div
        className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs animate-in fade-in"
        onClick={() => !isSubmitting && onClose()}
      />

      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl p-6 bg-card border border-border shadow-2xl z-10 space-y-4"
      >
        <div className="flex items-start justify-between border-b border-border/60 pb-3">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-primary/10 text-primary">
              <Receipt className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-base font-black text-foreground">
                Inward Vendor Invoice Simulation & Intake
              </h3>
              <p className="text-xs text-foreground-secondary">
                Ingest vendor invoice against PO commitment for automated 3-way matching.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="text-foreground-muted hover:text-foreground p-1 rounded-lg"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {errorMessage && (
          <div className="p-3 rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-800 text-xs font-bold flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 text-rose-600" />
            <span>{errorMessage}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 text-xs">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="font-bold text-foreground">Select Purchase Order *</label>
              <select
                value={formData.poId}
                onChange={(e) => {
                  const po = availablePos.find((p) => p.id === e.target.value);
                  if (po) handleSelectPo(po);
                }}
                className="w-full h-9 px-3 rounded-xl border border-border bg-surface text-foreground font-semibold text-xs focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="">{isLoadingPos ? 'Loading POs...' : '-- Choose PO --'}</option>
                {availablePos.map((po) => (
                  <option key={po.id} value={po.id}>
                    {po.poNumber} — {po.vendorName}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="font-bold text-foreground">Vendor Invoice Number *</label>
              <input
                type="text"
                required
                value={formData.vendorInvoiceNumber}
                onChange={(e) => setFormData((prev) => ({ ...prev, vendorInvoiceNumber: e.target.value }))}
                className="w-full h-9 px-3 rounded-xl border border-border bg-surface text-foreground font-mono font-bold text-xs focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            <div className="space-y-1">
              <label className="font-bold text-foreground">Invoice Date *</label>
              <input
                type="date"
                required
                value={formData.invoiceDate}
                onChange={(e) => setFormData((prev) => ({ ...prev, invoiceDate: e.target.value }))}
                className="w-full h-9 px-3 rounded-xl border border-border bg-surface text-foreground text-xs focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            <div className="space-y-1">
              <label className="font-bold text-foreground">Due Date *</label>
              <input
                type="date"
                required
                value={formData.dueDate}
                onChange={(e) => setFormData((prev) => ({ ...prev, dueDate: e.target.value }))}
                className="w-full h-9 px-3 rounded-xl border border-border bg-surface text-foreground text-xs focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>

          {/* Line Items Table */}
          <div className="space-y-2 pt-2 border-t border-border/40">
            <span className="font-extrabold uppercase text-[10px] text-foreground block">
              Billed Line Items
            </span>

            {formData.items.length === 0 ? (
              <div className="p-4 text-center text-xs text-foreground-muted">
                Select a PO above to populate expected line items.
              </div>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {formData.items.map((it, idx) => (
                  <div
                    key={it.poItemId || idx}
                    className="p-3 rounded-xl border border-border bg-surface/40 flex items-center justify-between gap-3 text-xs"
                  >
                    <div className="flex-1 min-w-0">
                      <span className="font-bold text-foreground truncate block">{it.description}</span>
                      <span className="text-[10px] text-foreground-muted font-mono">{it.materialCode}</span>
                    </div>

                    <div className="w-24">
                      <label className="text-[9px] font-bold text-foreground-secondary uppercase block">Qty</label>
                      <input
                        type="number"
                        min={1}
                        value={it.quantity}
                        onChange={(e) => handleItemQtyChange(idx, Number(e.target.value))}
                        className="w-full h-7 px-2 rounded-lg border border-border bg-card text-foreground font-mono font-bold text-xs"
                      />
                    </div>

                    <div className="w-28">
                      <label className="text-[9px] font-bold text-foreground-secondary uppercase block">Unit Price</label>
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={it.unitPrice}
                        onChange={(e) => handleItemPriceChange(idx, Number(e.target.value))}
                        className="w-full h-7 px-2 rounded-lg border border-border bg-card text-foreground font-mono font-bold text-xs"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-border/40">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-xl font-bold"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={isSubmitting || formData.items.length === 0}
              className="rounded-xl font-bold bg-primary text-white"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-1.5">
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" /> Ingesting...
                </span>
              ) : (
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4" /> Ingest Invoice
                </span>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
